// Pure date/time helpers ported from aistudio's LiveScopeSelection.vue.
// Kept framework-free so they're easy to unit test.

export const HOUR_MS = 3_600_000;

export const pad2 = (n: number): string => String(n).padStart(2, '0');

export const formatHHmm = (date: Date): string =>
  `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

export const toUTCISO = (date: Date): string => date.toISOString().replace(/\.\d{3}Z$/, 'Z');

// Builds a Date for today at the given 'HH:mm[:ss]' wall-clock time.
export function timeStrToTodayDate(timeStr: string): Date {
  const [hour = 0, minute = 0, second = 0] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hour, minute, second, 0);
  return date;
}

// 'today' / 'tomorrow' relative to `ref`, based on calendar day.
export function dayLabel(date: Date, ref: Date): 'today' | 'tomorrow' {
  const startOfRef = new Date(ref);
  startOfRef.setHours(0, 0, 0, 0);
  const startOfDate = new Date(date);
  startOfDate.setHours(0, 0, 0, 0);
  const diffDays = Math.round((startOfDate.getTime() - startOfRef.getTime()) / 86_400_000);
  return diffDays <= 0 ? 'today' : 'tomorrow';
}

// 'HH:mm:ss' of the top of the next hour from now.
export function getNextHourTimeStr(now: Date = new Date()): string {
  const next = new Date(now);
  next.setHours(now.getHours() + 1, 0, 0, 0);
  return `${pad2(next.getHours())}:${pad2(next.getMinutes())}:${pad2(next.getSeconds())}`;
}

// ms until the next minute boundary — used to align the ticking "now".
export function getMsUntilNextMinute(now: Date = new Date()): number {
  return (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
}

// Local UTC offset as '+05:30' / '-08:00', matching moment's `format('Z')` —
// the shape aistudio sends as `scheduledDataPayload.timeZone`.
// getTimezoneOffset() returns minutes *behind* UTC, so the sign is inverted.
export function getUtcOffsetStr(now: Date = new Date()): string {
  const offsetMinutes = -now.getTimezoneOffset();
  const sign = offsetMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes);
  return `${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}

/**
 * Resolves a live run's absolute UTC window from the wall-clock times the user
 * picked. Port of aistudio's `getLiveRunTimeRange` (client/services/custom-date.js).
 *
 * - NOW mode: the run starts at submit time.
 * - LATER mode: the start is that time today, rolled to tomorrow if it has
 *   already passed.
 * - The end is rolled past midnight whenever it lands before the (possibly
 *   already rolled) start.
 *
 * Deliberately called at submit time rather than reusing the ISO the Scope step
 * computed: in NOW mode that value ages for as long as the user spends on the
 * remaining steps, which would backdate the listening window.
 */
export function getLiveRunTimeRange(
  isLaterMode: boolean,
  startTimeStr: string | null,
  endTimeStr: string | null,
  now: Date = new Date()
): { startTime: string | null; endTime: string | null } {
  const reference = new Date(now);
  reference.setMilliseconds(0);

  const start =
    isLaterMode && startTimeStr ? timeStrToTodayDate(startTimeStr) : new Date(reference.getTime());
  if (isLaterMode && startTimeStr && start.getTime() < reference.getTime()) {
    start.setDate(start.getDate() + 1);
  }

  if (!endTimeStr) return { startTime: toUTCISO(start), endTime: null };

  const end = timeStrToTodayDate(endTimeStr);
  if (end.getTime() < start.getTime()) end.setDate(end.getDate() + 1);

  return { startTime: toUTCISO(start), endTime: toUTCISO(end) };
}

/**
 * Window sent to the live conversation-count *estimate* endpoint — distinct
 * from {@link getLiveRunTimeRange}, which resolves the actual future window
 * the run itself will listen over. Port of aistudio's `getLiveCountWindowRange`
 * (client/services/custom-date.js): since the real window is in the future,
 * there's no traffic to count yet, so the estimate instead looks 7 days back
 * at the same wall-clock hours as a proxy for expected volume.
 */
export function getLiveCountWindowRange(
  effectiveStart: Date | null,
  effectiveEnd: Date | null,
  now: Date = new Date()
): { startTime: string; endTime: string } {
  const start = new Date(now);
  start.setDate(start.getDate() - 7);
  if (effectiveStart) {
    start.setHours(effectiveStart.getHours(), effectiveStart.getMinutes(), 0, 0);
  } else {
    start.setHours(0, 0, 0, 0);
  }

  const end = new Date(now);
  if (effectiveEnd) {
    end.setHours(effectiveEnd.getHours(), effectiveEnd.getMinutes(), 0, 0);
  } else {
    end.setHours(23, 59, 59, 0);
  }

  return { startTime: toUTCISO(start), endTime: toUTCISO(end) };
}

export interface ListeningWindowSummary {
  duration: string;
  range: string;
}

// 'HH:mm:ss' → 'HH:mm'
export function formatTimeStrShort(timeStr: string | null | undefined): string {
  if (!timeStr) return '';
  return timeStr.slice(0, 5);
}

// ISO string → "5 Jun, 13:21", formatted en-US to match the rest of this
// feature's date/time formatting (see lib/format.ts).
export function formatLocalDateShort(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const datePart = d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
  const timePart = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return `${datePart}, ${timePart}`;
}

export function buildListeningWindowSummary(
  start: Date | null,
  end: Date | null,
  now: Date,
  isNowMode: boolean
): ListeningWindowSummary | null {
  if (!start || !end || end.getTime() <= start.getTime()) return null;
  const diffMs = end.getTime() - start.getTime();
  const hours = Math.floor(diffMs / HOUR_MS);
  const minutes = Math.round((diffMs % HOUR_MS) / 60_000);
  const parts: string[] = [];
  if (hours) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (minutes) parts.push(`${minutes} min`);
  const duration = parts.join(' ') || '0 min';
  const startLabel = isNowMode ? 'Run Start' : `${formatHHmm(start)} ${dayLabel(start, now)}`;
  const range = `${startLabel} – ${formatHHmm(end)} ${dayLabel(end, now)}`;
  return { duration, range };
}
